import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '@/config/api';
import { Loader2, ArrowLeft, AlertCircle } from 'lucide-react';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

type CheckoutType = 'new_subscription' | 'plan_change' | 'package_addon';

interface CheckoutState {
  clientSecret: string | null;
  checkoutType: CheckoutType;
  planName?: string;
  packageName?: string;
  priceUsd?: number;
  billingInterval?: string;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, license, isLoading: authLoading } = useAuth();

  const [checkoutState, setCheckoutState] = useState<CheckoutState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    const type = searchParams.get('type') as CheckoutType || 'new_subscription';
    const plan = searchParams.get('plan');
    const packageId = searchParams.get('package');
    const billing = searchParams.get('billing') || 'monthly';
    const includedPackageId = searchParams.get('included_package_id');
    const coupon = searchParams.get('coupon');

    if (!user?.email) {
      setError('Please sign in to continue');
      setIsLoading(false);
      return;
    }

    try {
      let endpoint: string;
      let body: Record<string, unknown>;

      if (type === 'plan_change' && plan && license?.license_key) {
        endpoint = API_ENDPOINTS.SUBSCRIBE_CHECKOUT_PLAN_CHANGE;
        body = {
          licenseKey: license.license_key,
          newPlan: plan,
          billingInterval: billing,
          uiMode: 'embedded',
          ...(includedPackageId ? { included_package_id: includedPackageId } : {}),
        };
      } else if (type === 'package_addon' && packageId && license?.license_key) {
        endpoint = API_ENDPOINTS.PACKAGES_ADD;
        body = {
          licenseKey: license.license_key,
          packageId,
          uiMode: 'embedded',
        };
      } else if (plan) {
        endpoint = API_ENDPOINTS.SUBSCRIBE_CHECKOUT;
        body = {
          plan,
          email: user.email,
          billingInterval: billing,
          uiMode: 'embedded',
          ...(includedPackageId ? { included_package_id: includedPackageId } : {}),
          ...(coupon ? { coupon } : {}),
        };
      } else {
        setError('Invalid checkout parameters');
        setIsLoading(false);
        return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (!data.client_secret) {
        throw new Error('No client secret returned from server');
      }

      setCheckoutState({
        clientSecret: data.client_secret,
        checkoutType: type,
        planName: data.plan || data.new_plan || plan,
        packageName: data.package_name,
        priceUsd: data.price_usd,
        billingInterval: billing,
      });
      setIsLoading(false);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize checkout');
      setIsLoading(false);
    }
  }, [searchParams, user?.email, license?.license_key]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/pricing');
      return;
    }

    if (!authLoading && isAuthenticated && user) {
      fetchClientSecret();
    }
  }, [authLoading, isAuthenticated, user, fetchClientSecret, navigate]);

  const handleBack = () => {
    const type = searchParams.get('type');
    if (type === 'package_addon') {
      navigate('/account?tab=subscription');
    } else {
      navigate('/pricing');
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
              <span className="mt-4 text-gray-600 dark:text-gray-400">
                Preparing checkout...
              </span>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Checkout Error
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                {error}
              </p>
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!checkoutState?.clientSecret) {
    return null;
  }

  const getTitle = () => {
    switch (checkoutState.checkoutType) {
      case 'plan_change':
        return `Upgrade to ${checkoutState.planName}`;
      case 'package_addon':
        return `Add ${checkoutState.packageName} Package`;
      default:
        return `Subscribe to ${checkoutState.planName}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0A1628]">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {getTitle()}
            </h1>
            {checkoutState.billingInterval && checkoutState.checkoutType !== 'package_addon' && (
              <p className="text-gray-600 dark:text-gray-400">
                Billing: {checkoutState.billingInterval === 'yearly' ? 'Annual' : 'Monthly'}
              </p>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret: checkoutState.clientSecret }}
            >
              <EmbeddedCheckout className="stripe-embedded-checkout" />
            </EmbeddedCheckoutProvider>
          </div>

          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
            Secure payment powered by Stripe. Your payment information is encrypted and secure.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
